import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  ClipboardList, MapPin, LogOut, Camera, Upload, X,
  Activity, Clock, CheckCircle, AlertCircle, CreditCard,
  Navigation, Bell, User, Calendar, FileText, QrCode,
  IndianRupee, Send, Image, Check, Eye, ChevronRight,
  Wallet, Receipt, CircleDollarSign, Phone, Mail, LocateFixed,
  MapPinOff, Settings, Shield, MessageCircle, Play, Pause
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const EnhancedStaffDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');
  const [locationSharing, setLocationSharing] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskDetail, setShowTaskDetail] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [taskSummary, setTaskSummary] = useState({ pending: 0, completed: 0, overdue: 0, due_today: 0 });
  const [currentLocation, setCurrentLocation] = useState(null);
  const fileInputRef = useRef(null);
  const watchIdRef = useRef(null);

  // Completion form state
  const [completionPhoto, setCompletionPhoto] = useState(null);
  const [completionPhotoPreview, setCompletionPhotoPreview] = useState(null);
  const [completionNotes, setCompletionNotes] = useState('');
  const [submittingCompletion, setSubmittingCompletion] = useState(false);

  // Payment form state
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [transactionId, setTransactionId] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('upi');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [submittingPayment, setSubmittingPayment] = useState(false);

  // Comments state
  const [taskComments, setTaskComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [sendingComment, setSendingComment] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('field_staff_token');
    const userData = localStorage.getItem('field_staff_user');
    
    if (!token || !userData) {
      navigate('/fieldstaff-monitor/staff-login');
      return;
    }

    const parsedUser = JSON.parse(userData);
    if (parsedUser.role !== 'staff') {
      navigate('/fieldstaff-monitor/staff-login');
      return;
    }

    setUser(parsedUser);
    fetchTasks();
    fetchTaskSummary();
    
    // Check location permission on load
    checkLocationPermission();

    const interval = setInterval(() => {
      fetchTasks();
      fetchTaskSummary();
    }, 30000);
    
    return () => {
      clearInterval(interval);
      // Stop location watching on unmount
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, [navigate]);

  // Check and request location permission
  const checkLocationPermission = async () => {
    if (!('geolocation' in navigator)) {
      setLocationError('Geolocation is not supported by your browser');
      return;
    }

    try {
      // Check permission status
      if ('permissions' in navigator) {
        const permission = await navigator.permissions.query({ name: 'geolocation' });
        
        if (permission.state === 'granted') {
          startLocationSharing();
        } else if (permission.state === 'prompt') {
          // Show modal to explain why we need location
          setShowLocationModal(true);
        } else if (permission.state === 'denied') {
          setLocationError('Location permission denied. Please enable it in your browser settings.');
          setLocationSharing(false);
        }

        // Listen for permission changes
        permission.onchange = () => {
          if (permission.state === 'granted') {
            startLocationSharing();
            setShowLocationModal(false);
          } else if (permission.state === 'denied') {
            setLocationError('Location permission denied');
            setLocationSharing(false);
          }
        };
      } else {
        // Fallback for browsers without permission API
        startLocationSharing();
      }
    } catch (error) {
      console.error('Permission check error:', error);
      // Try to start location sharing anyway
      startLocationSharing();
    }
  };

  // Request location permission and start tracking
  const requestLocationPermission = () => {
    setShowLocationModal(false);
    startLocationSharing();
  };

  const getAuthConfig = () => {
    const token = localStorage.getItem('field_staff_token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/field-staff/staff/my-tasks`, getAuthConfig());
      setTasks(response.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
      setLoading(false);
    }
  };

  const fetchTaskSummary = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/field-staff/staff/my-tasks-summary`, getAuthConfig());
      setTaskSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const startLocationSharing = () => {
    if (!('geolocation' in navigator)) {
      setLocationError('Geolocation not supported');
      return;
    }

    setLocationError(null);

    // Clear any existing watch
    if (watchIdRef.current) {
      navigator.geolocation.clearWatch(watchIdRef.current);
    }

    // Start continuous location tracking
    watchIdRef.current = navigator.geolocation.watchPosition(
      async (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        setCurrentLocation({ latitude, longitude, accuracy });
        
        try {
          await axios.post(
            `${API_URL}/api/field-staff/staff/share-location`,
            { latitude, longitude, accuracy },
            getAuthConfig()
          );
          setLocationSharing(true);
          setLocationError(null);
        } catch (error) {
          console.error('Location share error:', error);
          // Don't set locationSharing to false on API error, GPS is still working
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        setLocationSharing(false);
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setLocationError('Location permission denied. Please enable in browser settings.');
            break;
          case error.POSITION_UNAVAILABLE:
            setLocationError('Location unavailable. Please check GPS is enabled.');
            break;
          case error.TIMEOUT:
            setLocationError('Location request timed out. Retrying...');
            // Retry after timeout
            setTimeout(() => startLocationSharing(), 5000);
            break;
          default:
            setLocationError('Unable to get location');
        }
      },
      { 
        enableHighAccuracy: true, 
        timeout: 15000, 
        maximumAge: 10000 // Cache location for 10 seconds
      }
    );

    toast.success('Location sharing enabled');
  };

  const stopLocationSharing = () => {
    if (watchIdRef.current) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    setLocationSharing(false);
    setCurrentLocation(null);
    toast.info('Location sharing stopped');
  };

  const handleLogout = () => {
    localStorage.removeItem('field_staff_token');
    localStorage.removeItem('field_staff_user');
    navigate('/fieldstaff-monitor/staff-login');
  };

  // File upload handler
  const handlePhotoSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Photo size must be less than 10MB');
        return;
      }
      setCompletionPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => setCompletionPhotoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  // Upload photo and complete task
  const handleCompleteTask = async () => {
    if (!completionPhoto) {
      toast.error('Please upload a completion photo');
      return;
    }

    setSubmittingCompletion(true);
    try {
      // Upload file first
      const formData = new FormData();
      formData.append('files', completionPhoto);
      
      const uploadResponse = await axios.post(
        `${API_URL}/api/field-staff/staff/upload-files`,
        formData,
        { ...getAuthConfig(), headers: { ...getAuthConfig().headers, 'Content-Type': 'multipart/form-data' } }
      );

      const uploadedFile = uploadResponse.data.files?.[0];
      if (!uploadedFile) throw new Error('File upload failed');

      // Complete task
      await axios.post(
        `${API_URL}/api/field-staff/staff/my-tasks/${selectedTask.id}/complete`,
        {
          completion_photo: uploadedFile.file_id || uploadedFile.url || uploadedFile.id,
          completion_notes: completionNotes
        },
        getAuthConfig()
      );

      toast.success('Task marked as completed! Waiting for admin verification.');
      setShowCompleteModal(false);
      resetCompletionForm();
      fetchTasks();
      fetchTaskSummary();
    } catch (error) {
      console.error('Complete task error:', error);
      toast.error(error.response?.data?.detail || 'Failed to complete task');
    } finally {
      setSubmittingCompletion(false);
    }
  };

  // Fetch payment info
  const fetchPaymentInfo = async (taskId) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/field-staff/staff/my-tasks/${taskId}/payment-info`,
        getAuthConfig()
      );
      setPaymentInfo(response.data);
      setPaymentAmount(response.data.payment_amount?.toString() || '');
    } catch (error) {
      console.error('Payment info error:', error);
      toast.error(error.response?.data?.detail || 'Failed to fetch payment info');
    }
  };

  // Submit payment
  const handleSubmitPayment = async () => {
    if (!transactionId || !paymentAmount || !paymentMethod) {
      toast.error('Please fill all payment details');
      return;
    }

    setSubmittingPayment(true);
    try {
      await axios.post(
        `${API_URL}/api/field-staff/staff/my-tasks/${selectedTask.id}/submit-payment`,
        {
          transaction_id: transactionId,
          amount: parseFloat(paymentAmount),
          payment_method: paymentMethod,
          notes: paymentNotes
        },
        getAuthConfig()
      );

      toast.success('Payment submitted! Waiting for admin confirmation.');
      setShowPaymentModal(false);
      resetPaymentForm();
      fetchTasks();
    } catch (error) {
      console.error('Submit payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit payment');
    } finally {
      setSubmittingPayment(false);
    }
  };

  const resetCompletionForm = () => {
    setCompletionPhoto(null);
    setCompletionPhotoPreview(null);
    setCompletionNotes('');
  };

  const resetPaymentForm = () => {
    setPaymentInfo(null);
    setTransactionId('');
    setPaymentAmount('');
    setPaymentMethod('upi');
    setPaymentNotes('');
  };

  const openCompleteModal = (task) => {
    setSelectedTask(task);
    resetCompletionForm();
    setShowCompleteModal(true);
  };

  const openPaymentModal = async (task) => {
    setSelectedTask(task);
    resetPaymentForm();
    await fetchPaymentInfo(task.id);
    setShowPaymentModal(true);
  };

  const openTaskDetail = (task) => {
    setSelectedTask(task);
    setShowTaskDetail(true);
    fetchTaskComments(task.id);
  };

  // Fetch task comments
  const fetchTaskComments = async (taskId) => {
    try {
      setLoadingComments(true);
      const response = await axios.get(
        `${API_URL}/api/field-staff/staff/tasks/${taskId}/comments`,
        getAuthConfig()
      );
      setTaskComments(response.data.comments || []);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoadingComments(false);
    }
  };

  // Send a comment
  const handleSendComment = async () => {
    if (!newComment.trim() || !selectedTask) return;
    
    try {
      setSendingComment(true);
      await axios.post(
        `${API_URL}/api/field-staff/staff/tasks/${selectedTask.id}/comments`,
        { message: newComment },
        getAuthConfig()
      );
      
      setNewComment('');
      fetchTaskComments(selectedTask.id);
      toast.success('Message sent to admin');
    } catch (error) {
      console.error('Error sending comment:', error);
      toast.error('Failed to send message');
    } finally {
      setSendingComment(false);
    }
  };

  // Update task status
  const handleUpdateStatus = async (newStatus) => {
    if (!selectedTask) return;
    
    try {
      setUpdatingStatus(true);
      await axios.put(
        `${API_URL}/api/field-staff/staff/tasks/${selectedTask.id}/status`,
        { status: newStatus },
        getAuthConfig()
      );
      
      setSelectedTask({...selectedTask, status: newStatus});
      toast.success(`Task status updated to ${newStatus.replace('_', ' ')}`);
      fetchTasks();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Helper functions
  const getPriorityColor = (priority) => {
    const colors = {
      urgent: 'bg-red-500 text-white',
      high: 'bg-orange-500 text-white',
      medium: 'bg-yellow-500 text-black',
      low: 'bg-blue-500 text-white'
    };
    return colors[priority] || colors.medium;
  };

  const getStatusInfo = (status) => {
    const info = {
      new: { color: 'bg-blue-100 text-blue-700', label: 'New', icon: AlertCircle },
      pending: { color: 'bg-gray-100 text-gray-700', label: 'Pending', icon: Clock },
      in_progress: { color: 'bg-yellow-100 text-yellow-700', label: 'In Progress', icon: Activity },
      completed: { color: 'bg-purple-100 text-purple-700', label: 'Completed', icon: Check },
      verified: { color: 'bg-green-100 text-green-700', label: 'Verified', icon: CheckCircle },
      resolved: { color: 'bg-emerald-100 text-emerald-700', label: 'Resolved', icon: CheckCircle },
      closed: { color: 'bg-gray-200 text-gray-600', label: 'Closed', icon: CheckCircle }
    };
    return info[status] || info.new;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not set';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
    });
  };

  // Filter tasks
  const activeTasks = tasks.filter(t => ['new', 'pending', 'in_progress'].includes(t.status));
  const completedTasks = tasks.filter(t => t.status === 'completed');
  const verifiedTasks = tasks.filter(t => ['verified'].includes(t.status));
  const resolvedTasks = tasks.filter(t => ['resolved', 'closed'].includes(t.status));

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Activity className="h-12 w-12 animate-spin text-green-600 mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Loading your tasks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24" data-testid="staff-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white sticky top-0 z-40 shadow-lg">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h1 className="text-xl font-bold">My Tasks</h1>
              <p className="text-sm text-green-100">Welcome, {user?.name}</p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" className="text-white hover:bg-white/20">
                <Bell className="h-5 w-5" />
              </Button>
              <Button size="sm" variant="ghost" className="text-white hover:bg-white/20" onClick={handleLogout}>
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Location Status - Clickable to toggle */}
          <div 
            onClick={() => locationSharing ? stopLocationSharing() : startLocationSharing()}
            className={`flex items-center justify-between rounded-lg px-3 py-2 cursor-pointer transition-all ${
              locationSharing 
                ? 'bg-green-500/20 hover:bg-green-500/30' 
                : 'bg-red-500/20 hover:bg-red-500/30'
            }`}
          >
            <div className="flex items-center gap-2">
              <div className={`h-3 w-3 rounded-full ${locationSharing ? 'bg-green-300 animate-pulse' : 'bg-red-400'}`}></div>
              <div>
                <span className="text-sm font-medium">
                  {locationSharing ? 'Location Active' : 'Location Off'}
                </span>
                {locationError && (
                  <p className="text-xs text-red-200">{locationError}</p>
                )}
                {currentLocation && locationSharing && (
                  <p className="text-xs text-green-200">
                    GPS Accuracy: {Math.round(currentLocation.accuracy)}m
                  </p>
                )}
              </div>
            </div>
            {locationSharing ? (
              <LocateFixed className="h-5 w-5 text-green-300" />
            ) : (
              <MapPinOff className="h-5 w-5 text-red-300" />
            )}
          </div>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="px-4 py-4">
        <div className="grid grid-cols-4 gap-2">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-2 text-center">
              <p className="text-xl font-bold text-blue-600">{taskSummary.pending}</p>
              <p className="text-xs text-blue-700">Active</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
            <CardContent className="p-2 text-center">
              <p className="text-xl font-bold text-orange-600">{taskSummary.due_today}</p>
              <p className="text-xs text-orange-700">Today</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-2 text-center">
              <p className="text-xl font-bold text-green-600">{taskSummary.completed}</p>
              <p className="text-xs text-green-700">Done</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
            <CardContent className="p-2 text-center">
              <p className="text-xl font-bold text-red-600">{taskSummary.overdue}</p>
              <p className="text-xs text-red-700">Overdue</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Tasks List */}
      <div className="px-4">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-4 w-full mb-4 h-auto">
            <TabsTrigger value="active" className="text-xs py-2">
              <ClipboardList className="h-3 w-3 mr-1" />
              Active ({activeTasks.length})
            </TabsTrigger>
            <TabsTrigger value="completed" className="text-xs py-2">
              <Clock className="h-3 w-3 mr-1" />
              Review ({completedTasks.length})
            </TabsTrigger>
            <TabsTrigger value="verified" className="text-xs py-2">
              <CreditCard className="h-3 w-3 mr-1" />
              Payment ({verifiedTasks.length})
            </TabsTrigger>
            <TabsTrigger value="resolved" className="text-xs py-2">
              <CheckCircle className="h-3 w-3 mr-1" />
              Done ({resolvedTasks.length})
            </TabsTrigger>
          </TabsList>

          {/* Active Tasks */}
          <TabsContent value="active" className="space-y-3 mt-0">
            {activeTasks.length > 0 ? (
              activeTasks.map((task) => (
                <TaskCard 
                  key={task.id} 
                  task={task} 
                  onComplete={() => openCompleteModal(task)}
                  onViewDetail={() => openTaskDetail(task)}
                  getStatusInfo={getStatusInfo}
                  getPriorityColor={getPriorityColor}
                  formatDate={formatDate}
                />
              ))
            ) : (
              <EmptyState icon={ClipboardList} message="No active tasks" subtext="All caught up!" />
            )}
          </TabsContent>

          {/* Completed - Awaiting Verification */}
          <TabsContent value="completed" className="space-y-3 mt-0">
            {completedTasks.length > 0 ? (
              completedTasks.map((task) => (
                <TaskCard 
                  key={task.id} 
                  task={task}
                  onViewDetail={() => openTaskDetail(task)}
                  getStatusInfo={getStatusInfo}
                  getPriorityColor={getPriorityColor}
                  formatDate={formatDate}
                  showWaitingBadge
                />
              ))
            ) : (
              <EmptyState icon={Clock} message="No tasks awaiting review" subtext="Completed tasks will appear here" />
            )}
          </TabsContent>

          {/* Verified - Collect Payment */}
          <TabsContent value="verified" className="space-y-3 mt-0">
            {verifiedTasks.length > 0 ? (
              verifiedTasks.map((task) => (
                <TaskCard 
                  key={task.id} 
                  task={task}
                  onPayment={() => openPaymentModal(task)}
                  onViewDetail={() => openTaskDetail(task)}
                  getStatusInfo={getStatusInfo}
                  getPriorityColor={getPriorityColor}
                  formatDate={formatDate}
                  showPaymentButton
                />
              ))
            ) : (
              <EmptyState icon={CreditCard} message="No payments to collect" subtext="Verified tasks will appear here" />
            )}
          </TabsContent>

          {/* Resolved */}
          <TabsContent value="resolved" className="space-y-3 mt-0">
            {resolvedTasks.length > 0 ? (
              resolvedTasks.map((task) => (
                <TaskCard 
                  key={task.id} 
                  task={task}
                  onViewDetail={() => openTaskDetail(task)}
                  getStatusInfo={getStatusInfo}
                  getPriorityColor={getPriorityColor}
                  formatDate={formatDate}
                />
              ))
            ) : (
              <EmptyState icon={CheckCircle} message="No resolved tasks" subtext="Complete tasks to see them here" />
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Complete Task Modal */}
      {showCompleteModal && selectedTask && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center">
          <div className="bg-white w-full sm:w-[480px] sm:rounded-xl rounded-t-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-green-600 to-emerald-600 text-white p-4 sm:rounded-t-xl rounded-t-3xl">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  Complete Task
                </h2>
                <button onClick={() => setShowCompleteModal(false)} className="p-2 hover:bg-white/20 rounded-full">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-4 space-y-4">
              {/* Task Summary */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">{selectedTask.title}</h3>
                <p className="text-sm text-gray-600">{selectedTask.description}</p>
              </div>

              {/* Photo Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Completion Photo <span className="text-red-500">*</span>
                </label>
                
                {completionPhotoPreview ? (
                  <div className="relative">
                    <img 
                      src={completionPhotoPreview} 
                      alt="Preview" 
                      className="w-full h-48 object-cover rounded-xl border"
                    />
                    <button 
                      onClick={() => { setCompletionPhoto(null); setCompletionPhotoPreview(null); }}
                      className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full shadow-lg"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div 
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-green-500 hover:bg-green-50 transition-all"
                  >
                    <Camera className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                    <p className="text-gray-600 font-medium">Tap to take photo</p>
                    <p className="text-xs text-gray-500">or select from gallery</p>
                  </div>
                )}
                <input 
                  type="file" 
                  ref={fileInputRef}
                  accept="image/*"
                  capture="environment"
                  onChange={handlePhotoSelect}
                  className="hidden"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Completion Notes (Optional)
                </label>
                <textarea
                  value={completionNotes}
                  onChange={(e) => setCompletionNotes(e.target.value)}
                  placeholder="Add any notes about the completed task..."
                  className="w-full border rounded-xl p-3 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  rows={3}
                />
              </div>

              {/* Submit Button */}
              <Button 
                onClick={handleCompleteTask}
                disabled={!completionPhoto || submittingCompletion}
                className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 py-6 text-lg"
              >
                {submittingCompletion ? (
                  <Activity className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <CheckCircle className="w-5 h-5 mr-2" />
                )}
                {submittingCompletion ? 'Submitting...' : 'Mark as Completed'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedTask && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center">
          <div className="bg-white w-full sm:w-[480px] sm:rounded-xl rounded-t-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-4 sm:rounded-t-xl rounded-t-3xl">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold flex items-center gap-2">
                  <Wallet className="w-5 h-5" />
                  Collect Payment
                </h2>
                <button onClick={() => setShowPaymentModal(false)} className="p-2 hover:bg-white/20 rounded-full">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-4 space-y-4">
              {/* Task Info */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h3 className="font-semibold text-gray-900">{selectedTask.title}</h3>
                {paymentInfo?.admin_verification_notes && (
                  <p className="text-sm text-gray-600 mt-1">Note: {paymentInfo.admin_verification_notes}</p>
                )}
              </div>

              {/* Payment Amount */}
              <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl p-6 text-center">
                <p className="text-sm opacity-80 mb-1">Amount to Collect</p>
                <p className="text-4xl font-bold flex items-center justify-center">
                  <IndianRupee className="w-8 h-8" />
                  {paymentInfo?.payment_amount?.toLocaleString('en-IN') || '0'}
                </p>
              </div>

              {/* QR Code */}
              {paymentInfo?.payment_qr_code && (
                <div className="bg-gray-50 rounded-xl p-4 text-center">
                  <p className="text-sm text-gray-600 mb-2">Payment QR Code</p>
                  <div className="bg-white p-4 rounded-xl inline-block">
                    <img 
                      src={paymentInfo.payment_qr_code} 
                      alt="Payment QR" 
                      className="w-48 h-48 mx-auto"
                    />
                  </div>
                </div>
              )}

              {/* Payment Details Form */}
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                  <div className="grid grid-cols-3 gap-2">
                    {['upi', 'cash', 'qr_code'].map(method => (
                      <button
                        key={method}
                        onClick={() => setPaymentMethod(method)}
                        className={`p-3 rounded-xl border-2 text-center transition-all ${
                          paymentMethod === method 
                            ? 'border-purple-500 bg-purple-50 text-purple-700' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <span className="text-sm font-medium capitalize">{method.replace('_', ' ')}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Transaction ID</label>
                  <Input
                    value={transactionId}
                    onChange={(e) => setTransactionId(e.target.value)}
                    placeholder="Enter transaction/receipt ID"
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount Collected</label>
                  <div className="relative">
                    <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="number"
                      value={paymentAmount}
                      onChange={(e) => setPaymentAmount(e.target.value)}
                      placeholder="0.00"
                      className="pl-10"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
                  <Input
                    value={paymentNotes}
                    onChange={(e) => setPaymentNotes(e.target.value)}
                    placeholder="Any additional notes..."
                  />
                </div>
              </div>

              {/* Submit Button */}
              <Button 
                onClick={handleSubmitPayment}
                disabled={!transactionId || !paymentAmount || submittingPayment}
                className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 py-6 text-lg"
              >
                {submittingPayment ? (
                  <Activity className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <Send className="w-5 h-5 mr-2" />
                )}
                {submittingPayment ? 'Submitting...' : 'Submit Payment'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Task Detail Modal */}
      {showTaskDetail && selectedTask && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center">
          <div className="bg-white w-full sm:w-[480px] sm:rounded-xl rounded-t-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4 sm:rounded-t-xl rounded-t-3xl">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold">Task Details</h2>
                <button onClick={() => setShowTaskDetail(false)} className="p-2 hover:bg-white/20 rounded-full">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-lg text-gray-900">{selectedTask.title}</h3>
                <Badge className={getStatusInfo(selectedTask.status).color}>
                  {getStatusInfo(selectedTask.status).label}
                </Badge>
              </div>

              <p className="text-gray-600">{selectedTask.description}</p>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Priority</p>
                  <Badge className={getPriorityColor(selectedTask.priority)}>
                    {selectedTask.priority?.toUpperCase()}
                  </Badge>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Due Date</p>
                  <p className="text-sm font-medium">{formatDate(selectedTask.due_date)}</p>
                </div>
              </div>

              {/* Customer Contact Section */}
              {selectedTask.customer_contact && (
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                  <div className="flex items-center gap-2 mb-3">
                    <User className="w-5 h-5 text-blue-600" />
                    <p className="font-semibold text-blue-800">Customer Details</p>
                  </div>
                  
                  <div className="space-y-2">
                    {selectedTask.customer_contact.name && (
                      <p className="font-medium text-gray-900">{selectedTask.customer_contact.name}</p>
                    )}
                    
                    {selectedTask.customer_contact.address && (
                      <p className="text-sm text-gray-600 flex items-start gap-2">
                        <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" />
                        <span>{selectedTask.customer_contact.address}
                          {selectedTask.customer_contact.landmark && (
                            <span className="text-gray-500"> (Near: {selectedTask.customer_contact.landmark})</span>
                          )}
                        </span>
                      </p>
                    )}
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex gap-2 mt-4">
                    {/* Call Button */}
                    {selectedTask.customer_contact.phone && (
                      <Button
                        size="sm"
                        className="flex-1 bg-green-600 hover:bg-green-700"
                        onClick={() => window.open(`tel:${selectedTask.customer_contact.phone}`, '_self')}
                      >
                        <Phone className="w-4 h-4 mr-2" />
                        Call
                      </Button>
                    )}
                    
                    {/* Navigate Button */}
                    {(selectedTask.customer_contact.latitude && selectedTask.customer_contact.longitude) && (
                      <Button
                        size="sm"
                        className="flex-1 bg-blue-600 hover:bg-blue-700"
                        onClick={() => {
                          const url = `https://www.google.com/maps/dir/?api=1&destination=${selectedTask.customer_contact.latitude},${selectedTask.customer_contact.longitude}`;
                          window.open(url, '_blank');
                        }}
                      >
                        <Navigation className="w-4 h-4 mr-2" />
                        Navigate
                      </Button>
                    )}
                  </div>
                  
                  {/* Alternate Phone */}
                  {selectedTask.customer_contact.alternate_phone && (
                    <button
                      onClick={() => window.open(`tel:${selectedTask.customer_contact.alternate_phone}`, '_self')}
                      className="mt-2 text-xs text-blue-600 hover:underline flex items-center gap-1"
                    >
                      <Phone className="w-3 h-3" />
                      Alt: {selectedTask.customer_contact.alternate_phone}
                    </button>
                  )}
                </div>
              )}

              {/* Legacy custom_fields support */}
              {selectedTask.custom_fields && !selectedTask.customer_contact && (
                <div className="space-y-2">
                  {selectedTask.custom_fields.customer_name && (
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500">Customer</p>
                      <p className="font-medium">{selectedTask.custom_fields.customer_name}</p>
                      {selectedTask.custom_fields.customer_address && (
                        <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                          <MapPin className="w-3 h-3" />
                          {selectedTask.custom_fields.customer_address}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Payment Amount */}
              {(selectedTask.custom_fields?.payment_amount || selectedTask.payment_amount) && (
                <div className="bg-green-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Payment Amount</p>
                  <p className="text-lg font-bold text-green-700 flex items-center">
                    <IndianRupee className="w-4 h-4" />
                    {(selectedTask.custom_fields?.payment_amount || selectedTask.payment_amount)?.toLocaleString('en-IN')}
                  </p>
                </div>
              )}

              {selectedTask.staff_completion_photo && (
                <div>
                  <p className="text-xs text-gray-500 mb-2">Completion Photo</p>
                  <img 
                    src={selectedTask.staff_completion_photo} 
                    alt="Completion" 
                    className="w-full h-48 object-cover rounded-lg"
                  />
                </div>
              )}

              {selectedTask.admin_verification_notes && (
                <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-3">
                  <p className="text-xs text-blue-600 font-medium">Admin Notes</p>
                  <p className="text-sm text-gray-700">{selectedTask.admin_verification_notes}</p>
                </div>
              )}

              {/* Status Update Section */}
              {['pending', 'in_progress'].includes(selectedTask.status) && (
                <div className="border-t pt-4">
                  <p className="text-sm font-medium text-gray-700 mb-3">Update Status</p>
                  <div className="flex gap-2">
                    {selectedTask.status === 'pending' && (
                      <Button
                        size="sm"
                        onClick={() => handleUpdateStatus('in_progress')}
                        disabled={updatingStatus}
                        className="flex-1 bg-orange-500 hover:bg-orange-600"
                      >
                        <Play className="w-4 h-4 mr-1" />
                        Start Task
                      </Button>
                    )}
                    {selectedTask.status === 'in_progress' && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleUpdateStatus('pending')}
                          disabled={updatingStatus}
                          className="flex-1"
                        >
                          <Pause className="w-4 h-4 mr-1" />
                          Pause
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => {
                            setShowTaskDetail(false);
                            openCompleteModal(selectedTask);
                          }}
                          className="flex-1 bg-green-500 hover:bg-green-600"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Complete
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Comments Section */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <MessageCircle className="w-4 h-4" />
                    Messages with Admin
                  </p>
                  {taskComments.filter(c => c.sender_role !== 'staff' && !c.read_by_staff).length > 0 && (
                    <Badge className="bg-red-500">
                      {taskComments.filter(c => c.sender_role !== 'staff' && !c.read_by_staff).length} new
                    </Badge>
                  )}
                </div>
                
                {/* Comments List */}
                <div className="bg-gray-50 rounded-lg p-3 max-h-48 overflow-y-auto space-y-2 mb-3">
                  {loadingComments ? (
                    <div className="text-center py-4">
                      <Activity className="w-5 h-5 animate-spin mx-auto text-gray-400" />
                    </div>
                  ) : taskComments.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">No messages yet. Start a conversation with your admin.</p>
                  ) : (
                    taskComments.map((comment, idx) => (
                      <div 
                        key={idx} 
                        className={`p-2 rounded-lg max-w-[85%] ${
                          comment.sender_role === 'staff' 
                            ? 'bg-blue-100 ml-auto' 
                            : 'bg-white border'
                        }`}
                      >
                        <p className="text-xs text-gray-500 mb-1">
                          {comment.sender_role === 'staff' ? 'You' : comment.sender_name}
                          {' • '}
                          {new Date(comment.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                        </p>
                        <p className="text-sm text-gray-800">{comment.message}</p>
                      </div>
                    ))
                  )}
                </div>

                {/* New Comment Input */}
                <div className="flex gap-2">
                  <Input
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Type a message to admin..."
                    className="flex-1"
                    onKeyPress={(e) => e.key === 'Enter' && handleSendComment()}
                  />
                  <Button
                    onClick={handleSendComment}
                    disabled={!newComment.trim() || sendingComment}
                    size="icon"
                    className="bg-blue-600"
                  >
                    {sendingComment ? (
                      <Activity className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Location Permission Modal */}
      {showLocationModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-sm rounded-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full mx-auto flex items-center justify-center mb-4">
                <LocateFixed className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-xl font-bold text-white">Enable Location</h2>
            </div>
            
            <div className="p-6 space-y-4">
              <p className="text-gray-600 text-center">
                To help your admin track your field activities and ensure your safety, we need access to your location.
              </p>
              
              <div className="space-y-2">
                <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                  <Shield className="w-5 h-5 text-blue-600" />
                  <p className="text-sm text-gray-700">Your location is only shared with your admin</p>
                </div>
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <MapPin className="w-5 h-5 text-green-600" />
                  <p className="text-sm text-gray-700">Helps optimize task assignments based on your location</p>
                </div>
                <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                  <Navigation className="w-5 h-5 text-purple-600" />
                  <p className="text-sm text-gray-700">Enables live location tracking on the dashboard</p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowLocationModal(false)}
                >
                  Not Now
                </Button>
                <Button
                  className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600"
                  onClick={requestLocationPermission}
                >
                  <LocateFixed className="w-4 h-4 mr-2" />
                  Enable
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-3 z-40">
        <div className="grid grid-cols-4 gap-2">
          {[
            { value: 'active', icon: ClipboardList, label: 'Active' },
            { value: 'completed', icon: Clock, label: 'Review' },
            { value: 'verified', icon: CreditCard, label: 'Payment' },
            { value: 'resolved', icon: CheckCircle, label: 'Done' }
          ].map(tab => (
            <Button
              key={tab.value}
              variant={activeTab === tab.value ? 'default' : 'ghost'}
              size="sm"
              className={`flex-col h-auto py-2 ${activeTab === tab.value ? 'bg-green-600 text-white' : ''}`}
              onClick={() => setActiveTab(tab.value)}
            >
              <tab.icon className="h-5 w-5 mb-1" />
              <span className="text-xs">{tab.label}</span>
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
};

// Task Card Component
const TaskCard = ({ task, onComplete, onPayment, onViewDetail, getStatusInfo, getPriorityColor, formatDate, showWaitingBadge, showPaymentButton }) => {
  const StatusIcon = getStatusInfo(task.status).icon;

  return (
    <Card className="hover:shadow-md transition-shadow" data-testid={`task-card-${task.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`h-2 w-2 rounded-full ${getPriorityColor(task.priority)} mt-2`}></div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-2">
              <h3 className="font-semibold text-sm">{task.title}</h3>
              <Badge className={`text-xs ${getStatusInfo(task.status).color}`}>
                <StatusIcon className="w-3 h-3 mr-1" />
                {getStatusInfo(task.status).label}
              </Badge>
            </div>
            
            <p className="text-xs text-gray-600 mb-3 line-clamp-2">{task.description}</p>
            
            <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
              <div className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                <span>{formatDate(task.due_date)}</span>
              </div>
              {task.custom_fields?.payment_amount && (
                <div className="flex items-center gap-1 text-green-600 font-medium">
                  <IndianRupee className="h-3 w-3" />
                  <span>{task.custom_fields.payment_amount.toLocaleString('en-IN')}</span>
                </div>
              )}
            </div>

            {task.is_overdue && !['completed', 'verified', 'resolved', 'closed'].includes(task.status) && (
              <Badge className="bg-red-100 text-red-700 text-xs mb-2">
                <AlertCircle className="w-3 h-3 mr-1" />
                Overdue
              </Badge>
            )}

            {showWaitingBadge && (
              <Badge className="bg-purple-100 text-purple-700 text-xs mb-2">
                <Clock className="w-3 h-3 mr-1" />
                Waiting for Admin Verification
              </Badge>
            )}

            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                className="flex-1"
                onClick={onViewDetail}
              >
                <Eye className="h-4 w-4 mr-1" />
                Details
              </Button>
              
              {onComplete && !['completed', 'verified', 'resolved', 'closed'].includes(task.status) && (
                <Button
                  size="sm"
                  className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600"
                  onClick={onComplete}
                >
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Complete
                </Button>
              )}

              {showPaymentButton && onPayment && (
                <Button
                  size="sm"
                  className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600"
                  onClick={onPayment}
                >
                  <Wallet className="h-4 w-4 mr-1" />
                  Collect
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Empty State Component
const EmptyState = ({ icon: Icon, message, subtext }) => (
  <Card>
    <CardContent className="p-12 text-center">
      <Icon className="h-16 w-16 text-gray-300 mx-auto mb-3" />
      <h3 className="font-semibold text-gray-700 mb-1">{message}</h3>
      <p className="text-sm text-gray-500">{subtext}</p>
    </CardContent>
  </Card>
);

export default EnhancedStaffDashboard;
